<template>
  <ElAutocomplete
    v-model="searchInput"
    :fetch-suggestions="guestSearch"
    :debounce="400"
    :trigger-on-focus="true"
    resize="both"
    clearable
    placeholder="Search for guests"
    popper-class="search-results-container"
    inputmode="search"
    class="search-input"
    @select="onSearchResultSelect"
  >
    <!--@keyup.enter="onSearchInput"-->
    <!--@input="onSearchInput"-->
    <div slot="prefix">
      <FontAwesomeIcon
        :icon="['far', 'search']"
        class="h-40 text-primary-light ml-10"
      />
    </div>

    <template
      v-if="!guestIsInitialLoading"
      v-slot="{ item }"
    >
      <GuestListItem
        v-if="item.type !== 'message'"
        :item="item.guest"
        :item-index="item.index"
        :item-height="55"
        :avatar-chip-size="40"
        :hide-check-in="checkInDisabled"
        hide-edit
        hide-delete
        @guest-update="$emit('guest-update', $event)"
        @guest-check-in="onCheckIn('guest-check-in', $event)"
        @guest-reset-check-in="onCheckIn('guest-reset-check-in', $event)"
        @guest-extras="$emit('guest-extras', $event)"
      />
      <p
        v-else
        class="flex items-center text-base text-primary-light py-10 px-0 mx-20"
      >
        <FontAwesomeIcon
          v-if="item.icon"
          :icon="['fas', 'info-circle']"
          class="text-2xl mr-15"
        />
        <span class="whitespace-normal">
          {{ item.text }}
        </span>
      </p>
    </template>
  </ElAutocomplete>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Fuse from 'fuse.js';
import GuestListItem from './list/GuestListItem';

/**
 * What a guest can be found by. `fullName` leads because it is the string the list itself
 * prints, and so the string people type at a door. It was missing from the index until
 * 2026-10-14: Fuse scores each key against the WHOLE query, so a two-word query was measured
 * against one-word fields and lost. On a real 1,169-guest list, 66% of guests could not be
 * found by typing their own full name while their row sat visible underneath the search box.
 */
const SEARCH_FIELDS = [
  'fullName', 'firstName', 'lastName', 'email',
  'phoneNumber', 'organization', 'jobTitle', 'note', 'barcodeValue',
];

/**
 * Lowercased and stripped of diacritics, so "Nunez" finds "Núñez" and "Gonzalez" finds
 * "González". Guest lists are routinely typed on a keyboard that cannot produce the accents
 * they contain -- 42% of the names at the event that prompted this carried one.
 */
function foldForSearch(value) {
  return `${value}`.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/**
 * Built once per guest object. The store replaces the object whenever the guest changes, so a
 * stale haystack can never be read, and a WeakMap lets it be collected along with it.
 */
const haystacks = new WeakMap();
function haystackFor(guest) {
  let haystack = haystacks.get(guest);

  if (haystack === undefined) {
    haystack = foldForSearch(SEARCH_FIELDS.map((field) => guest[field] || '').join(' '));
    haystacks.set(guest, haystack);
  }

  return haystack;
}

/**
 * How good a match is, lowest first. Only the first 40 results are shown, so on a common
 * first name ("Ana" matches 30 guests, "José" 106) the person actually named that has to come
 * ahead of everyone who merely contains it -- otherwise the fix for one search breaks another.
 */
function rankFor(guest, words, query) {
  const name = foldForSearch(guest.fullName || '');

  if (name === query) return 0;
  if (name.startsWith(query)) return 1;

  const nameWords = name.split(/\s+/);

  if (words.every((word) => nameWords.some((part) => part.startsWith(word)))) return 2;
  if (words.every((word) => name.includes(word))) return 3;

  return 4; // matched on email, company or notes rather than on the name
}

export default {
  props: {
    firstNameFirstOrder: {
      type: Boolean,
      default: false,
    },
    checkInDisabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    GuestListItem,
  },
  data: () => ({
    searchInput: '',
    guestCountLimit: 40,
  }),
  computed: {
    ...mapState('guest', {
      guestIsInitialLoading: 'isInitialLoading',
      // Deliberately every guest, not the filtered subset. A status filter is a view of the
      // list; search is a lookup. Searching only inside the filter meant that selecting
      // "Attending" -- the obvious thing to do at a door -- silently hid everyone else from
      // the search, and the filtered array is only recomputed when the filter selection
      // changes, so it went stale as guests were checked in.
      allGuests: 'guests',
    }),
  },
  methods: {
    ...mapActions('guest', {
      updateGuestPhotoURL: 'updateGuestPhotoURL',
    }),
    onSearchResultSelect(item) {
      if (item.type === 'message') return;

      this.searchInput = '';
    },
    /**
     * Results are a frozen snapshot (Element UI gives the slot a plain copy), so a row cannot
     * show the check-in it just performed. Clearing the box closes the dropdown instead of
     * leaving a stale "Attending" next to a guest who is now in -- and leaves the cursor ready
     * for the next name, which is the whole job at a door.
     */
    onCheckIn(event, guestId) {
      this.$emit(event, guestId);
      this.searchInput = '';
    },
    guestSearch(queryString, callback) {
      let messageText = '';

      if (queryString !== undefined && queryString.length < 3) {
        if (queryString.length === 0) {
          messageText = 'Type the first/last name, email, phone number, company, job title or notes';
        } else {
          messageText = `Enter ${3 - queryString.length} more character${(3 - queryString.length) === 1 ? '' : 's'}`;
        }

        callback([{
          type: 'message',
          text: messageText,
          icon: queryString.length === 0,
        }]);
        return;
      }

      const searchResults = this.searchInGuest(queryString);
      const limitedSearchResults = searchResults.slice(0, this.guestCountLimit);
      const results = [];

      limitedSearchResults.forEach((searchResult) => {
        results.push({
          value: searchResult.item.firstName,
          label: searchResult.item.id,
          index: results.length - 1,
          guest: searchResult.item,
        });
        // Bug: The guest data here is not reactive (Limitation of Element UI's component)

        // Update guest photoURLs of filtered results if not previously set
        if (searchResult.item.photoStoragePath && !searchResult.item.photoURL) {
          this.updateGuestPhotoURL(searchResult.item.listIndex);
        }
      });

      if (searchResults.length > this.guestCountLimit) {
        results.push({
          type: 'message',
          text: `+ ${searchResults.length - this.guestCountLimit} more`,
        });
      }

      callback(results);
    },
    /**
     * Every word of the query has to appear somewhere in the guest, in any order, so
     * "Abraham Hazoury" and "Hazoury Abraham" both find the same person and a middle name in
     * the list does not break a search for the two names around it.
     */
    matchByWords(queryString) {
      const words = foldForSearch(queryString).split(/\s+/).filter(Boolean);

      if (!words.length) return [];

      const query = words.join(' ');

      return this.allGuests
        .filter((guest) => {
          const haystack = haystackFor(guest);

          return words.every((word) => haystack.includes(word));
        })
        .map((item) => ({ item, rank: rankFor(item, words, query) }))
        .sort((a, b) => a.rank - b.rank
          || `${a.item.fullName}`.length - `${b.item.fullName}`.length);
    },
    /** Typo tolerance, and only that: it runs when nothing matched the words as they were typed. */
    matchFuzzily(queryString) {
      const options = {
        shouldSort: true,
        includeMatches: true,
        threshold: 0.3,
        location: 0,
        // Fuse 3 has no ignoreLocation; a large distance lets words match anywhere in longer notes
        distance: 1000,
        maxPatternLength: 32,
        minMatchCharLength: 1,
        // Field names as normalized by the guest store (store/modules/guest.js normalizeGuestData):
        // Firestore phone -> phoneNumber, company -> organization, jobPosition -> jobTitle, notes -> note
        keys: [{
          name: 'fullName',
          weight: 1,
        }, {
          name: 'firstName',
          weight: this.firstNameFirstOrder ? 0.9 : 0.8,
        }, {
          name: 'lastName',
          weight: this.firstNameFirstOrder ? 0.8 : 0.9,
        }, {
          name: 'email',
          weight: 0.6,
        }, {
          name: 'phoneNumber',
          weight: 0.4,
        }, {
          name: 'organization',
          weight: 0.4,
        }, {
          name: 'jobTitle',
          weight: 0.4,
        }, {
          name: 'note',
          weight: 0.4,
        }],
      };
      const fuse = new Fuse(this.allGuests, options);

      return fuse.search(queryString);
    },
    searchInGuest(queryString) {
      const byWords = this.matchByWords(queryString);

      return byWords.length ? byWords : this.matchFuzzily(queryString);
    },
  },
};
</script>

<style>
/* Autocomplete input */
.search-input {
  .el-input__inner {
    @apply pl-50 bg-gray-300 rounded !important;

    &::placeholder {
      @apply text-primary tracking-wide opacity-90;
    }

    &:focus {
      @apply border-primary-light;
    }
  }

  .el-input__prefix {
    @apply text-lg text-primary-light;
  }
}

/* El-Autocomplete */
.search-results-container.el-autocomplete-suggestion {
  @apply scrolling-touch;

  .el-autocomplete-suggestion__wrap {
    max-height: 315px;

    li[role="option"] {
      @apply leading-tight p-0;

      &:hover {
        @apply bg-gray-100;
      }
    }
  }
}
</style>
